import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import usePageDetails from '../hooks/PageDetails'
import { isMetaPage } from '../util/util';

const re = /^(?:.*\/)?wiki\/([^#\n]*)(?:#(.*))?$/;

const WikiTooltip = ({ containerRef, target, settings }) => {
  const [hoveredAnchor, setHoveredAnchor] = useState(null);
  const [mousePos, setMousePos] = useState(null);

  const path = decodeURIComponent(hoveredAnchor?.getAttribute('href'));
  const match = path?.match(re);
  const title = (match?.length > 1) ? match[1]?.replaceAll('_', ' ') : '';
  const section = (match?.length > 2) ? match[2]?.replaceAll('_', ' ') : '';

  const [pageDetails] = usePageDetails(title);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onOver = (e) => {
      const anchor = e.target.closest('a');
      if (anchor) {
        setHoveredAnchor(anchor);
        setMousePos({ x: e.clientX, y: e.clientY });
      } else {
        setHoveredAnchor(null);
        setMousePos(null);
      }
    };

    const onOut = (e) => {
      const related = e.relatedTarget;
      if (!related || !container.contains(related)) {
        setHoveredAnchor(null);
        setMousePos(null);
      }
    };

    container.addEventListener('mouseover', onOver);
    container.addEventListener('mouseout', onOut);
    return () => {
      container.removeEventListener('mouseover', onOver);
      container.removeEventListener('mouseout', onOut);
    };
  }, [containerRef]);

  if (!hoveredAnchor || !mousePos) return null;

  const rectMid = rect => [
    rect.x + (rect.width / 2),
    rect.y + (rect.height / 2),
  ];

  const rectMouseProximity = rect => {
    const [rx, ry] = rectMid(rect);
    return (rx - mousePos.x)**2 + (ry - mousePos.y)**2;
  };

  const bbs = [...hoveredAnchor.getClientRects()];
  const bb = (bbs?.length > 1)
    ? bbs.reduce((a, b) => rectMouseProximity(a) < rectMouseProximity(b) ? a : b)
    : hoveredAnchor.getBoundingClientRect();

  const [x, y] = bb ? rectMid(bb) : [-1000, -1000];

  const isTarget = title === target?.title || pageDetails?.id == target?.id;
  const isIllegalMeta = !settings?.metaPages && isMetaPage(path);

  let wikiClassName = 'link-mouseover';
  if (isTarget) wikiClassName += ' target';
  if (isIllegalMeta) wikiClassName += ' illegal';

  return createPortal(
    <div className={wikiClassName} style={{ left: x + window.scrollX, top: y + window.scrollY }}>
      <span className='title'>{title + (section ? ` @ ${section}` : '')}</span>
      {pageDetails?.redirect && (
        <>
          <span className='redirect'>▼ Redirects to ▼</span>
          <span className='title'>{pageDetails.redirect.to}</span>
        </>
      )}
      <span className='description'>
        {pageDetails
          ? pageDetails.description ?? 'No description'
          : 'Loading description...'}
      </span>
      {isIllegalMeta && (
        <span className='illegal-note'>{'(Disallowed meta page)'}</span>
      )}
    </div>,
    document.body
  );
}

export default WikiTooltip;
